import React from 'react';
import { Box, Container, Typography, Grid, Link as MuiLink } from '@mui/material';
import Img from './Img';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { colors } from '../theme';
import { track } from '../utils/analytics';
import { servicePages, findPage } from '../data/pages';

const SECTIONS = [
    { label: 'Services', id: 'services' },
    { label: 'How we work', id: 'story' },
    { label: 'Work', id: 'work' },
    { label: 'About', id: 'about' },
];

const linkSx = {
    display: 'block',
    py: 0.75,
    mb: 0.25,
    textAlign: 'left',
    fontSize: '0.9rem',
    color: 'text.secondary',
    textDecoration: 'none',
    cursor: 'pointer',
    width: 'fit-content',
    '&:hover': { color: colors.green },
};

const Footer = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const goToSection = (id) => {
        track('nav_click', { item: id, source: 'footer' });
        if (location.pathname === '/') {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate(`/#${id}`);
        }
    };

    return (
        <Box component="footer" sx={{ borderTop: '1px solid #e2e8f0', pt: { xs: 6, md: 7 }, pb: 4 }}>
            <Container maxWidth="lg">
                <Grid container spacing={{ xs: 4, md: 6 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Img
                            src="/media/minneai-logo.webp"
                            alt="minneAI"
                            width={800} height={214}
                            sx={{ maxWidth: 170, height: 'auto', display: 'block', mb: 2 }}
                        />
                        <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', lineHeight: 1.7, maxWidth: 320 }}>
                            Software engineering and AI consulting. We build custom software,
                            put AI to work inside it, and run it on real devices.
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 5, md: 2 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
                            Explore
                        </Typography>
                        {SECTIONS.map((s) => (
                            <MuiLink key={s.id} component={RouterLink} to={`/#${s.id}`} onClick={(e) => { if (e.button === 0 && !e.metaKey && !e.ctrlKey) { e.preventDefault(); goToSection(s.id); } }} sx={linkSx}>
                                {s.label}
                            </MuiLink>
                        ))}
                        <MuiLink component={RouterLink} to={findPage('working-with-us').path} onClick={() => track('nav_click', { item: 'working-with-us', source: 'footer' })} sx={linkSx}>
                            Working with us
                        </MuiLink>
                    </Grid>

                    <Grid size={{ xs: 7, md: 3 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
                            Services
                        </Typography>
                        {servicePages.map((p) => (
                            <MuiLink key={p.id} component={RouterLink} to={p.path} onClick={() => track('nav_click', { item: p.id, source: 'footer' })} sx={linkSx}>
                                {p.label}
                            </MuiLink>
                        ))}
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
                            Get in touch
                        </Typography>
                        <MuiLink component={RouterLink} to="/Contact" onClick={() => track('cta_click', { location: 'footer' })} sx={linkSx}>
                            Schedule a consultation
                        </MuiLink>
                        <MuiLink
                            href="https://maps.google.com/?cid=15962025236700814684"
                            target="_blank" rel="noopener"
                            onClick={() => track('outbound_click', { item: 'google_reviews', source: 'footer' })}
                            sx={linkSx}
                        >
                            Reviews on Google
                        </MuiLink>
                        <MuiLink
                            href="https://www.linkedin.com/company/minneai-llc/"
                            target="_blank" rel="noopener"
                            onClick={() => track('outbound_click', { item: 'linkedin', source: 'footer' })}
                            sx={linkSx}
                        >
                            LinkedIn
                        </MuiLink>
                    </Grid>
                </Grid>

                <Box sx={{ mt: { xs: 5, md: 7 }, pt: 3, borderTop: '1px solid #eef2f1' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                        © 2026 minneAI LLC &nbsp;·&nbsp; Software Engineering &amp; AI Consulting
                        &nbsp;·&nbsp; Minneapolis, Minnesota &nbsp;·&nbsp;{' '}
                        <MuiLink
                            component={RouterLink}
                            to="/privacy"
                            onClick={() => track('nav_click', { item: 'privacy', source: 'footer' })}
                            sx={{
                                display: 'inline-block', py: 0.5, color: 'inherit',
                                textDecoration: 'underline', textDecorationColor: 'rgba(0,0,0,0.25)', textUnderlineOffset: 3,
                                '&:hover': { color: colors.green, textDecorationColor: colors.green },
                            }}
                        >
                            Privacy
                        </MuiLink>
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;
